// src/hooks/useBeyondAnalytics.js

import { useState, useEffect, useMemo, useRef } from 'react';
import { PredictorFactory } from '../components/analytics/PredictorFactory';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { PREDICTION_METHODS } from '../components/analytics/PredictorFactory';

/**
 * Calculate Mean Absolute Percentage Error (MAPE)
 * @param {Array} predictions - Predicted values
 * @param {Array} actuals - Actual values
 * @param {string} valueColumn - Column name for values
 * @returns {number} - MAPE score (0-100)
 */
function calculateMAPE(predictions, actuals, valueColumn, dateColumn) {
  try {
    let sumAPE = 0;
    let validCount = 0;
    
    console.log('MAPE Debug - Predictions:', predictions.length, 'Actuals:', actuals.length);
    
    // Simple index-based matching
    const numComparisons = Math.min(predictions.length, actuals.length);
    
    for (let i = 0; i < numComparisons; i++) {
      const predicted = predictions[i][valueColumn];
      const actual = actuals[i][valueColumn];
      
      console.log(`MAPE comparison ${i}:`, {
        predDate: predictions[i][dateColumn],
        actualDate: actuals[i][dateColumn],
        predicted,
        actual
      });
      
      // Skip if actual is 0 or null
      if (actual && actual !== 0 && predicted !== null && predicted !== undefined) {
        const ape = Math.abs((actual - predicted) / actual) * 100;
        sumAPE += ape;
        validCount++;
      }
    }
    
    console.log(`MAPE calculation: ${validCount} valid comparisons out of ${numComparisons} possible`);
    return validCount > 0 ? sumAPE / validCount : Infinity;
  } catch (error) {
    console.error('Error calculating MAPE:', error);
    return Infinity;
  }
}

/**
 * Custom hook for processing Beyond Analytics predictions
 * @param {Array} data - Original query result data
 * @param {Object} schema - Original query schema
 * @param {Object} beyondAnalytics - Analytics configuration from backend
 * @returns {Object} - { enhancedData, enhancedSchema, isProcessing, error }
 */
export function useBeyondAnalytics(data, schema, beyondAnalytics, documentId) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [processedResult, setProcessedResult] = useState(null);
  const processingRef = useRef(false);
  const processingCacheRef = useRef(new Map());
  const lastProcessedKeyRef = useRef(null);

// Add debug logging at the top of the hook
console.log('[useBeyondAnalytics] Hook called with:', {
  hasData: !!data,
  dataLength: data?.length,
  hasSchema: !!schema,
  hasBeyondAnalytics: !!beyondAnalytics,
  analyticsRequired: beyondAnalytics?.analyticsRequired
});

// Create stable reference for analytics config
const analyticsConfigRef = useRef(null);
const analyticsKey = useMemo(() => {
  if (!beyondAnalytics) return 'no-analytics';
  if (!beyondAnalytics.analyticsRequired) return 'analytics-disabled';
  if (!beyondAnalytics.functions?.length) return 'no-functions';
  
  // Include timestamp to force recalculation on each query run
  const timestamp = beyondAnalytics.beyondAnalyticsTimestamp || beyondAnalytics.beyondAgentTimestamp || '';
  
  const key = beyondAnalytics.functions
    .map(f => `${f.name}-${f.config?.method || 'default'}-${f.config?.predictUntil || 'none'}`)
    .join('|') + `-${timestamp}`;
  
  // Only update ref if key actually changed
  if (analyticsConfigRef.current !== key) {
    analyticsConfigRef.current = key;
  }
  
  return key;
}, [beyondAnalytics]);

// Create stable data fingerprint
const dataFingerprintRef = useRef(null);
const dataKey = useMemo(() => {
  if (!data || !Array.isArray(data)) return 'no-data';
  if (data.length === 0) return 'empty-data';
  
  // Create a more stable fingerprint
  const firstRow = data[0];
  const lastRow = data[data.length - 1];
  const fingerprint = `${data.length}-${firstRow ? Object.keys(firstRow).length : 0}-${lastRow ? Object.keys(lastRow).length : 0}`;
  
  // Only update if fingerprint actually changed
  if (dataFingerprintRef.current !== fingerprint) {
    dataFingerprintRef.current = fingerprint;
  }
  
  return fingerprint;
}, [data]);

// Schema fingerprint
const schemaFingerprintRef = useRef(null);
const schemaKey = useMemo(() => {
  if (!schema?.fields) return 'no-schema';
  
  const fingerprint = `${schema.fields.length}-${schema.fields.map(f => f.name).join(',')}`;
  
  if (schemaFingerprintRef.current !== fingerprint) {
    schemaFingerprintRef.current = fingerprint;
  }
  
  return fingerprint;
}, [schema]);
  
  // Process analytics when inputs change
  useEffect(() => {
    // Create composite key for caching
    const compositeKey = `${dataKey}-${schemaKey}-${analyticsKey}`;
    
    console.log('[useBeyondAnalytics] Effect triggered with composite key:', compositeKey);
  
    // Check if we already processed this exact configuration
    if (lastProcessedKeyRef.current === compositeKey) {
      console.log('[useBeyondAnalytics] Already processed this configuration, skipping...');
      return;
    }
  
    // Prevent re-processing if already processing
    if (processingRef.current) {
      console.log('[useBeyondAnalytics] Already processing, skipping...');
      return;
    }
  
    // Check cache
    const cachedResult = processingCacheRef.current.get(compositeKey);
    if (cachedResult) {
      console.log('[useBeyondAnalytics] Using cached result');
      setProcessedResult(cachedResult);
      return;
    }
  
    // Reset if no analytics needed
    if (!beyondAnalytics || !beyondAnalytics.analyticsRequired || !data || !schema) {
      console.log('[useBeyondAnalytics] Missing required data, resetting...');
      setProcessedResult(null);
      setError(null);
      return;
    }
  
    // Process analytics
    const processAnalytics = async () => {
      processingRef.current = true;
      setIsProcessing(true);
      const timeoutId = setTimeout(() => {
        if (processingRef.current) {
          console.error('[useBeyondAnalytics] Processing timeout - forcefully stopping');
          processingRef.current = false;
          setIsProcessing(false);
          setError('Processing timeout');
        }
      }, 10000); // 10 second timeout
      setError(null);

      try {
        console.log('Processing Beyond Analytics:', beyondAnalytics);
        
        // Validate we have functions to process
        if (!beyondAnalytics.functions || beyondAnalytics.functions.length === 0) {
          console.log('No analytics functions to process');
          setProcessedResult(null);
          return;
        }

        // Start with original data
        let enhancedData = [...data];
        let enhancedSchema = { ...schema, fields: [...schema.fields] };
        
        // Track all predictions for metadata
        const allPredictions = [];

        // Track which predictor was used from BE config
        let primaryPredictorMethod = null;
        let primaryDateColumn = null;
        let primaryValueColumn = null;
        let primaryConfig = null;

        // Process each analytics function
        for (const analyticsFunction of beyondAnalytics.functions) {
          try {
            console.log(`Processing analytics function: ${analyticsFunction.name}`);
            
            // Extract configuration
            const { name: functionName, config: functionConfig, visualization } = analyticsFunction;
            
            // Handle time series prediction
            if (functionName === 'timeSeriesPredict') {
              const { dateColumn, valueColumn, method = 'linear_regression', ...otherConfig } = functionConfig;
              
              // Store primary predictor info
              primaryPredictorMethod = method;
              primaryDateColumn = dateColumn;
              primaryValueColumn = valueColumn;
              primaryConfig = otherConfig;
              
              // Validate columns exist
              if (!dateColumn || !valueColumn) {
                throw new Error(`Missing required columns for ${functionName}`);
              }

              let predictionResult = null;
              let actualMethod = method;
              
              // Extract common config that applies to all predictors
              const commonConfig = {
                numPredictions: otherConfig.numPredictions,
                predictUntil: otherConfig.predictUntil,
                includeConfidenceInterval: otherConfig.includeConfidenceInterval,
                confidenceLevel: otherConfig.confidenceLevel,
                resolution: otherConfig.resolution,
                aggregationMethod: otherConfig.aggregationMethod
              };

              // ALL methods should receive their method-specific parameters
              // This is the most generic and robust approach
              const predictorConfig = { ...commonConfig, ...otherConfig };

              console.log('Passing config to predictor:', method, predictorConfig);

              try {
                // Try the requested method first
                const predictor = PredictorFactory.createPredictor(method, predictorConfig);
                predictionResult = predictor.predict(enhancedData, dateColumn, valueColumn);
              } catch (methodError) {
                console.warn(`Failed to use ${method}: ${methodError.message}`);
                
                // Define fallback strategies with appropriate configurations
                const fallbackStrategies = {
                  'arima': [
                    {
                      method: 'exponential_smoothing',
                      config: {
                        ...commonConfig,
                        ...otherConfig, // Include all ARIMA parameters
                        smoothingMethod: 'auto',
                        seasonalPeriod: otherConfig.seasonalPeriod
                      }
                    },
                    {
                      method: 'moving_average',
                      config: {
                        ...commonConfig,
                        ...otherConfig, // Include all ARIMA parameters
                        windowSize: 3, // Default window size
                        predictionMethod: 'trend' // Use trend-following for ARIMA-like behavior
                      }
                    },
                    {
                      method: 'linear_regression',
                      config: commonConfig // Linear regression uses common config
                    }
                  ],
                  'exponential_smoothing': [
                    {
                      method: 'arima',
                      config: {
                        ...commonConfig,
                        p: 1,
                        d: 1,
                        q: 1
                      }
                    },
                    {
                      method: 'moving_average',
                      config: {
                        ...commonConfig,
                        windowSize: 3,
                        predictionMethod: 'trend'
                      }
                    }
                  ],
                  'moving_average': [
                    {
                      method: 'exponential_smoothing',
                      config: {
                        ...commonConfig,
                        smoothingMethod: 'simple'
                      }
                    },
                    {
                      method: 'linear_regression',
                      config: commonConfig
                    }
                  ],
                  'linear_regression': [] // No fallback
                };
                
                const fallbacks = fallbackStrategies[method.toLowerCase()] || fallbackStrategies[method] || [];
                
                // Try fallback methods
                for (const fallback of fallbacks) {
                  try {
                    console.log(`Attempting fallback to ${fallback.method} with config:`, fallback.config);
                    const fallbackPredictor = PredictorFactory.createPredictor(fallback.method, fallback.config);
                    predictionResult = fallbackPredictor.predict(enhancedData, dateColumn, valueColumn);
                    actualMethod = fallback.method;
                    console.log(`Successfully used fallback method: ${fallback.method}`);
                    break;
                  } catch (fallbackError) {
                    console.warn(`Fallback ${fallback.method} also failed: ${fallbackError.message}`);
                  }
                }
                
                // If all methods failed, throw the original error
                if (!predictionResult) {
                  throw methodError;
                }
              }
              
              if (predictionResult && predictionResult.predictions) {
                console.log(`Generated ${predictionResult.predictions.length} predictions using ${actualMethod}`);
                
                // Create forecast column name - PRIMARY forecast keeps simple name
                const forecastColumn = `${valueColumn} - forecast`;

                console.log('Forecast column created:', {
                  originalColumn: valueColumn,
                  forecastColumn: forecastColumn,
                  predictionCount: predictionResult.predictions.length,
                  enhancedDataLength: enhancedData.length,
                  schemaFields: enhancedSchema.fields.map(f => f.name)
                });
                
                // Add forecast column to schema if not exists
                if (!enhancedSchema.fields.find(f => f.name === forecastColumn)) {
                  const originalField = enhancedSchema.fields.find(f => f.name === valueColumn);
                  enhancedSchema.fields.push({
                    name: forecastColumn,
                    type: originalField ? originalField.type : 'FLOAT',
                    isForecast: true,
                    originalColumn: valueColumn,
                    predictorMethod: actualMethod
                  });
                }

                // Add this debug log after adding forecast column to schema
                console.log('Schema after adding forecast column:', {
                  fieldCount: enhancedSchema.fields.length,
                  fieldNames: enhancedSchema.fields.map(f => f.name),
                  forecastField: enhancedSchema.fields.find(f => f.name === forecastColumn)
                });

                // Create a map of existing data by date for easy lookup
                const existingDataMap = new Map();
                enhancedData.forEach(row => {
                  const dateValue = row[dateColumn];
                  existingDataMap.set(dateValue, row);
                });
                
                // Process predictions
                predictionResult.predictions.forEach(prediction => {
                  const dateValue = prediction[dateColumn];
                  
                  // Check if we already have data for this date
                  if (existingDataMap.has(dateValue)) {
                    // Add forecast value to existing row
                    existingDataMap.get(dateValue)[forecastColumn] = prediction[valueColumn];
                  } else {
                    // Create new row with forecast data
                    const newRow = {};
                    
                    // Copy all columns from prediction
                    Object.keys(prediction).forEach(key => {
                      if (key === valueColumn) {
                        // Move the predicted value to forecast column
                        newRow[forecastColumn] = prediction[key];
                        newRow[key] = null; // Original column is null for future dates
                      } else {
                        newRow[key] = prediction[key];
                      }
                    });
                    
                    // Ensure all other columns exist as null
                    enhancedSchema.fields.forEach(field => {
                      if (!(field.name in newRow)) {
                        newRow[field.name] = null;
                      }
                    });
                    
                    enhancedData.push(newRow);
                  }
                });

                // IMPORTANT: Ensure ALL rows have the forecast column, even if null
                enhancedData.forEach(row => {
                  if (!(forecastColumn in row)) {
                    row[forecastColumn] = null;
                  }
                });
                
                // Store prediction info with actual method used
                const predictionInfo = {
                  function: functionName,
                  method: actualMethod,
                  requestedMethod: method,
                  columns: { x: dateColumn, y: valueColumn, forecast: forecastColumn },
                  count: predictionResult.predictions.length,
                  metadata: predictionResult.metadata,
                  visualization: visualization,
                  fallbackUsed: actualMethod !== method,
                  isPrimaryForecast: true // Temporarily mark as primary, will be updated by cross-validation
                };

                allPredictions.push(predictionInfo);

                // Add this debug log after processing all predictions
                console.log('Final enhanced data sample:', {
                  totalRows: enhancedData.length,
                  columns: Object.keys(enhancedData[0] || {}),
                  allColumns: enhancedData[0] ? Object.getOwnPropertyNames(enhancedData[0]) : [],
                  hasOwnProperty: enhancedData[0] ? enhancedData[0].hasOwnProperty(forecastColumn) : false,
                  lastRows: enhancedData.slice(-3).map(row => ({
                    [dateColumn]: row[dateColumn],
                    [valueColumn]: row[valueColumn],
                    [forecastColumn]: row[forecastColumn]
                  })),
                  firstRowKeys: enhancedData[0] ? Object.keys(enhancedData[0]) : [],
                  lastRowKeys: enhancedData[enhancedData.length - 1] ? Object.keys(enhancedData[enhancedData.length - 1]) : []
                });

                // Also check a specific row
                if (enhancedData.length > 0) {
                  console.log('First row structure:', enhancedData[0]);
                  console.log('Last row structure:', enhancedData[enhancedData.length - 1]);
                }

                // Update Firestore if fallback was used
                if (actualMethod !== method && documentId) {
                  try {
                    const db = getFirestore();
                    const docRef = doc(db, 'LLM Queries', documentId);
                    
                    // Update the beyondAnalytics field with fallback information
                    await updateDoc(docRef, {
                      'beyondAnalytics.fallbackUsed': true,
                      'beyondAnalytics.functions': beyondAnalytics.functions.map((f, fIdx) => {
                        if (fIdx === beyondAnalytics.functions.indexOf(analyticsFunction)) {
                          return {
                            ...f,
                            actualMethod: actualMethod,
                            requestedMethod: method,
                            fallbackUsed: true
                          };
                        }
                        return f;
                      })
                    });
                    
                    console.log('Updated Firestore with fallback information');
                  } catch (error) {
                    console.error('Failed to update Firestore with fallback info:', error);
                    // Don't throw - continue processing even if update fails
                  }
                }
              }
            }
            // After processing BE-specified predictors, run all other available predictors
        if (primaryDateColumn && primaryValueColumn && primaryPredictorMethod) {
          console.log('Running additional predictors for comprehensive forecasting...');
          
          // Get all available predictor methods
          const allMethods = PredictorFactory.getAvailableMethods();
          
          // Filter out the primary method already used
          const additionalMethods = allMethods.filter(method => 
            method.toLowerCase() !== primaryPredictorMethod.toLowerCase()
          );
          
          console.log(`Running ${additionalMethods.length} additional predictors:`, additionalMethods);
          
          // Get method display names
          const methodDisplayNames = {
            'linear_regression': 'Linear Regression',
            'moving_average': 'Moving Average',
            'arima': 'ARIMA',
            'exponential_smoothing': 'Exponential Smoothing'
          };
          
          // Run each additional predictor
          for (const method of additionalMethods) {
            try {
              console.log(`Running additional predictor: ${method}`);
              
              // Get default config for this method
              const defaultConfig = {
                numPredictions: primaryConfig.numPredictions || 10,
                predictUntil: primaryConfig.predictUntil,
                includeConfidenceInterval: false, // Keep additional forecasts simpler
                resolution: primaryConfig.resolution,
                aggregationMethod: primaryConfig.aggregationMethod || 'average'
              };
              
              // Add method-specific defaults
              if (method === 'moving_average') {
                defaultConfig.windowSize = 3;
                defaultConfig.predictionMethod = 'trend';
              } else if (method === 'arima') {
                defaultConfig.p = 1;
                defaultConfig.d = 1;
                defaultConfig.q = 1;
              } else if (method === 'exponential_smoothing') {
                defaultConfig.smoothingMethod = 'auto';
              }
              
              // Create predictor and run prediction
              const predictor = PredictorFactory.createPredictor(method, defaultConfig);
              const additionalResult = predictor.predict(enhancedData, primaryDateColumn, primaryValueColumn);
              
              if (additionalResult && additionalResult.predictions) {
                // Create forecast column name with predictor type
                const displayName = methodDisplayNames[method] || method;
                const forecastColumn = `${primaryValueColumn} - forecast (${displayName})`;
                
                console.log(`Generated ${additionalResult.predictions.length} predictions using ${method}`);
                
                // Add forecast column to schema
                if (!enhancedSchema.fields.find(f => f.name === forecastColumn)) {
                  const originalField = enhancedSchema.fields.find(f => f.name === primaryValueColumn);
                  enhancedSchema.fields.push({
                    name: forecastColumn,
                    type: originalField ? originalField.type : 'FLOAT',
                    isForecast: true,
                    originalColumn: primaryValueColumn,
                    predictorMethod: method,
                    isPrimaryForecast: false
                  });
                }
                
                // Create a map of existing data by date
                const existingDataMap = new Map();
                enhancedData.forEach((row, index) => {
                  const dateValue = row[primaryDateColumn];
                  existingDataMap.set(dateValue, index);
                });
                
                // Process predictions
                additionalResult.predictions.forEach(prediction => {
                  const dateValue = prediction[primaryDateColumn];
                  
                  // Check if we already have data for this date
                  if (existingDataMap.has(dateValue)) {
                    // Add forecast value to existing row
                    const rowIndex = existingDataMap.get(dateValue);
                    enhancedData[rowIndex][forecastColumn] = prediction[primaryValueColumn];
                  } else {
                    // Find if this date already exists in a prediction row
                    const existingPredRow = enhancedData.find(row => 
                      row[primaryDateColumn] === dateValue && row._isPrediction
                    );
                    
                    if (existingPredRow) {
                      // Add to existing prediction row
                      existingPredRow[forecastColumn] = prediction[primaryValueColumn];
                    } else {
                      // This shouldn't happen if all predictors use same date range
                      console.warn(`Date ${dateValue} not found in enhanced data for ${method}`);
                    }
                  }
                });
                
                // Ensure all rows have the forecast column
                enhancedData.forEach(row => {
                  if (!(forecastColumn in row)) {
                    row[forecastColumn] = null;
                  }
                });
                
                // Store prediction info
                allPredictions.push({
                  function: functionName,
                  method: method,
                  columns: { 
                    x: primaryDateColumn, 
                    y: primaryValueColumn, 
                    forecast: forecastColumn 
                  },
                  count: additionalResult.predictions.length,
                  metadata: additionalResult.metadata,
                  isPrimaryForecast: false // Will be updated by cross-validation if needed
                });
              }
            } catch (error) {
              console.warn(`Failed to run ${method} predictor:`, error.message);
              // Continue with other predictors even if one fails
            }
          }
        }
        
        // Run cross-validation to select best predictor
        console.log('Cross-validation check:', {
          hasPrimaryDateColumn: !!primaryDateColumn,
          hasPrimaryValueColumn: !!primaryValueColumn,
          enhancedDataLength: enhancedData.length,
          originalDataLength: data?.length
        });
        
        if (primaryDateColumn && primaryValueColumn && enhancedData.length >= 10) {
          console.log('Running cross-validation for predictor selection...');
          
          const mapeScores = {};
          const validationSize = 5;
          
          // Get original data without predictions - use the data parameter which is the original query result
          const originalDataOnly = data.filter(row => {
            // Only include rows that have actual values (not null) for the value column
            const hasValue = row[primaryValueColumn] !== null && row[primaryValueColumn] !== undefined;
            if (!hasValue) {
              console.log('Filtered out row with null value:', row[primaryDateColumn]);
            }
            return hasValue;
          });
          
          console.log('Original data after filtering:', {
            totalRows: originalDataOnly.length,
            firstRow: originalDataOnly[0],
            lastRow: originalDataOnly[originalDataOnly.length - 1]
          });
          
          // Adjust validation size based on available data
          const minDataPoints = 6; // Minimum to do any validation
          const adjustedValidationSize = Math.min(validationSize, Math.floor(originalDataOnly.length * 0.3));
          
          console.log('Validation sizing:', {
            originalValidationSize: validationSize,
            adjustedValidationSize: adjustedValidationSize,
            dataLength: originalDataOnly.length
          });
          
          if (originalDataOnly.length >= minDataPoints && adjustedValidationSize >= 2) {
            const trainingData = originalDataOnly.slice(0, -adjustedValidationSize);
            const validationData = originalDataOnly.slice(-adjustedValidationSize);
            
            console.log('Cross-validation data split:', {
              originalLength: originalDataOnly.length,
              trainingLength: trainingData.length,
              validationLength: validationData.length,
              lastTraining: trainingData[trainingData.length - 1],
              firstValidation: validationData[0]
            });
            
            // Test each predictor
            const allAvailableMethods = PredictorFactory.getAvailableMethods();
            for (const method of allAvailableMethods) {
              try {
                // Create predictor with same config but limited predictions
                const validationConfig = {
                  ...primaryConfig,
                  numPredictions: adjustedValidationSize,
                  includeConfidenceInterval: false
                };
                
                // Add method-specific defaults
                if (method === 'moving_average') {
                  validationConfig.windowSize = 3;
                  validationConfig.predictionMethod = 'trend';
                } else if (method === 'arima') {
                  validationConfig.p = 1;
                  validationConfig.d = 1;
                  validationConfig.q = 1;
                } else if (method === 'exponential_smoothing') {
                  validationConfig.smoothingMethod = 'auto';
                }
                
                const predictor = PredictorFactory.createPredictor(method, validationConfig);
                const validationResult = predictor.predict(trainingData, primaryDateColumn, primaryValueColumn);
                
                if (validationResult && validationResult.predictions) {
                  const mape = calculateMAPE(validationResult.predictions, validationData, primaryValueColumn, primaryDateColumn);
                  mapeScores[method] = mape;
                  console.log(`MAPE for ${method}: ${mape.toFixed(2)}%`);
                }
              } catch (error) {
                console.warn(`Cross-validation failed for ${method}:`, error.message);
                mapeScores[method] = Infinity;
              }
            }
            
            // Find best MAPE score
            if (Object.keys(mapeScores).length > 0) {
              const bestMethod = Object.entries(mapeScores)
                .filter(([_, score]) => score !== Infinity)
                .sort(([, a], [, b]) => a - b)[0]?.[0];
              
              // NEW: Find the best between ARIMA and Exponential Smoothing
              const arimaScore = mapeScores['arima'] || Infinity;
              const expSmoothingScore = mapeScores['exponential_smoothing'] || Infinity;
              
              let displayDefaultMethod = null;
              if (arimaScore !== Infinity || expSmoothingScore !== Infinity) {
                displayDefaultMethod = arimaScore <= expSmoothingScore ? 'arima' : 'exponential_smoothing';
                console.log(`Display default predictor: ${displayDefaultMethod} (ARIMA: ${arimaScore.toFixed(2)}%, Exp Smoothing: ${expSmoothingScore.toFixed(2)}%)`);
              }
              
              if (bestMethod) {
                console.log(`Best predictor based on MAPE: ${bestMethod} (${mapeScores[bestMethod].toFixed(2)}%)`);
                
                // Update isPrimaryForecast flags
                allPredictions.forEach(pred => {
                  pred.isPrimaryForecast = pred.method === bestMethod;
                  // NEW: Add flag for display default
                  pred.isDisplayDefault = pred.method === displayDefaultMethod;
                });
                
                // Log to Firestore if documentId available
                if (documentId) {
                  try {
                    const db = getFirestore();
                    const docRef = doc(db, 'LLM Queries', documentId);
                    
                    await updateDoc(docRef, {
                      'beyondAnalytics.crossValidation': {
                        mapeScores: mapeScores,
                        selectedMethod: bestMethod,
                        displayDefaultMethod: displayDefaultMethod, // NEW
                        originalMethod: primaryPredictorMethod,
                        timestamp: new Date().toISOString()
                      }
                    });
                    
                    console.log('Cross-validation results saved to Firestore');
                  } catch (error) {
                    console.error('Failed to save cross-validation results:', error);
                  }
                }
              }
            }
          }
        }
            
            // Future: Add other function types here
            // else if (functionName === 'anomalyDetection') { ... }
            // else if (functionName === 'clustering') { ... }
            else {
              console.warn(`Unknown analytics function: ${functionName}`);
            }
            
          } catch (functionError) {
            console.error(`Error processing function ${analyticsFunction.name}:`, functionError);
            // Continue with other functions even if one fails
          }
        }

        // DO NOT add metadata fields to schema
        // Keep schema clean for UI rendering
        if (allPredictions.length > 0) {
          console.log('Generated predictions metadata:', allPredictions);
          // Metadata is stored in allPredictions but not added to schema
        }

        // Set the processed result
        const result = {
          data: enhancedData,
          schema: enhancedSchema,
          predictions: allPredictions,
          originalDataCount: data.length,
          enhancedDataCount: enhancedData.length,
          // NEW: Add display default forecast column
          displayDefaultForecast: allPredictions.find(p => p.isDisplayDefault)?.columns?.forecast || null
        };

        setProcessedResult(result);
        processingCacheRef.current.set(compositeKey, result);
        lastProcessedKeyRef.current = compositeKey;

        // Limit cache size
        if (processingCacheRef.current.size > 10) {
          const firstKey = processingCacheRef.current.keys().next().value;
          processingCacheRef.current.delete(firstKey);
        }

        } catch (err) {
          console.error('Error in Beyond Analytics processing:', err);
          setError(err.message || 'Failed to process analytics');
          setProcessedResult(null);
        } finally {
          clearTimeout(timeoutId);
          setIsProcessing(false);
          processingRef.current = false;
        }
    };

    // Run processing
    processAnalytics();
    
    // Cleanup function
    return () => {
      console.log('[useBeyondAnalytics] Cleanup called');
      // Ensure we reset the processing flag if component unmounts
      processingRef.current = false;
    };
  }, [dataKey, schemaKey, analyticsKey]); // Use stable keys instead
  
  // Return enhanced data or original data
const result = useMemo(() => {
  if (processedResult) {
    // Log metadata for debugging but don't expose it in the data
    if (processedResult.predictions && processedResult.predictions.length > 0) {
      console.log('Prediction metadata:', processedResult.predictions);
    }
    
    return {
      enhancedData: processedResult.data,
      enhancedSchema: processedResult.schema,
      isProcessing,
      error,
      hasPredictions: processedResult.predictions && processedResult.predictions.length > 0,
      predictionMetadata: processedResult.predictions || [],
      displayDefaultForecast: processedResult.displayDefaultForecast // NEW
    };
  }

  // Return original data if no processing done
  return {
    enhancedData: data,
    enhancedSchema: schema,
    isProcessing,
    error,
    hasPredictions: false,
    predictionMetadata: [],
    displayDefaultForecast: null // NEW
  };
}, [data, schema, processedResult, isProcessing, error]);

  return result;
}


export default useBeyondAnalytics;